<template>
  <div>
    <div v-if="loading" class="text-center py-4">
      <RotateSquare size="60px" class="loading-position animated fadeIn"></RotateSquare>
    </div>

    <div v-else>
      <p class="text-muted small">
        A arte da capa é gerada por IA a partir do município da conta (marcos, vegetação e
        paisagem urbana) e composta com o título, a chamada e as logos abaixo. Ela substitui a
        capa padrão no PMAU completo exportado.
      </p>

      <!-- ── Logos ─────────────────────────────────────────────────────────── -->
      <h6 class="text-uppercase text-muted mt-3 mb-2"><i class="fa fa-images mr-1"></i> Logos da capa</h6>
      <div v-for="slot in logos" :key="slot.code" class="d-flex align-items-center mb-2">
        <div style="width: 230px">
          <strong class="small">{{ slot.label }}</strong>
          <b-badge v-if="slot.code === 'header_logo_url' && !slot.url" variant="warning" class="ml-1">
            Faltando
          </b-badge>
        </div>
        <img
          v-if="slot.url"
          :src="slot.url"
          class="mr-3"
          style="max-height: 44px; max-width: 130px; border: 1px solid #dee2e6; border-radius: 4px; background: #fff;"
          @error="(e) => (e.target.style.display = 'none')"
        />
        <label :for="'cover-logo-' + slot.code" class="btn btn-primary btn-sm mb-0 cursor-pointer">
          <i class="fa fa-upload mr-1"></i>{{ slot.url ? "Trocar" : "Enviar" }}
        </label>
        <input
          :id="'cover-logo-' + slot.code"
          type="file"
          accept="image/*"
          class="d-none"
          :disabled="!slot.variable_id"
          @change="(e) => uploadLogo(e, slot)"
        />
      </div>

      <!-- ── Chamada / slogan ──────────────────────────────────────────────── -->
      <div class="form-group mt-3" style="max-width: 480px">
        <label><strong>Chamada / slogan</strong> <span class="text-muted small">(opcional)</span></label>
        <div class="d-flex">
          <input
            v-model="tagline"
            type="text"
            class="form-control mr-2"
            maxlength="200"
            placeholder="Ex.: Capital do Pantanal"
          />
          <button
            class="btn btn-sm btn-success text-nowrap flex-shrink-0 d-inline-flex align-items-center"
            :disabled="savingTagline"
            @click="saveTagline"
          >
            <i class="fa fa-save mr-1"></i>{{ savingTagline ? "Salvando..." : "Salvar" }}
          </button>
        </div>
      </div>

      <!-- ── Geração ───────────────────────────────────────────────────────── -->
      <div class="d-flex align-items-center mt-3">
        <button class="btn btn-success btn-sm" :disabled="generating" @click="generate">
          <i class="fa fa-magic mr-1"></i>
          {{ generating ? "Gerando..." : imageUrl ? "Gerar novamente" : "Gerar capa" }}
        </button>
        <button
          v-if="imageUrl"
          class="btn btn-sm btn-word ml-2"
          :disabled="exporting"
          @click="exportDocx"
        >
          <i class="fa fa-file-word mr-1"></i>
          {{ exporting ? "Exportando..." : "Baixar capa (.docx)" }}
        </button>
      </div>
      <div v-if="generating" class="text-muted small mt-2">
        <i class="fa fa-spinner fa-spin mr-1"></i>
        Buscando referências do município e gerando a arte — pode levar até 1–2 minutos.
      </div>

      <!-- ── Preview ───────────────────────────────────────────────────────── -->
      <div class="mt-3">
        <div v-if="imageUrl">
          <img
            :src="imageUrl"
            style="max-width: 320px; width: 100%; border: 1px solid #dee2e6; border-radius: 6px;"
          />
          <div v-if="generatedAt" class="text-muted small mt-1">Gerada em {{ formatDate(generatedAt) }}</div>
        </div>
        <div v-else class="text-muted small">Nenhuma capa gerada ainda.</div>
      </div>
    </div>
  </div>
</template>

<script>
import RotateSquare from "../../components/RotateSquare";
import PmauService from "../../servico/PmauService";

export default {
  name: "PmauCover",
  components: { RotateSquare },
  props: {
    contaId: { type: String, required: true }
  },
  data() {
    return {
      loading: false,
      generating: false,
      savingTagline: false,
      exporting: false,
      tagline: "",
      imageUrl: null,
      generatedAt: null,
      logos: []
    };
  },
  beforeMount() {
    this.load();
  },
  methods: {
    load() {
      if (!this.contaId) return;
      this.loading = true;
      PmauService.GetCover(this.contaId)
        .then(r => {
          this.tagline = r.data.tagline || "";
          this.imageUrl = r.data.image_url || null;
          this.generatedAt = r.data.generated_at || null;
          this.logos = r.data.logos || [];
        })
        .catch(() => {
          this.$notify({ data: ["Erro ao carregar a capa do PMAU."], type: "error" });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    uploadLogo(event, slot) {
      const file = event.target.files[0];
      event.target.value = "";
      if (!file || !slot.variable_id) return;
      PmauService.UploadVariableImage(this.contaId, slot.variable_id, file)
        .then(r => {
          this.$set(slot, "url", r.data.value);
          this.$notify({ data: ["Logo enviada."], type: "success", duration: 2000 });
        })
        .catch(() => {
          this.$notify({ data: ["Erro ao enviar a logo."], type: "error" });
        });
    },
    saveTagline() {
      this.savingTagline = true;
      PmauService.SaveCover(this.contaId, { tagline: this.tagline })
        .then(r => {
          this.tagline = r.data.tagline || "";
          this.$notify({ data: ["Chamada salva."], type: "success", duration: 2000 });
        })
        .catch(() => {
          this.$notify({ data: ["Erro ao salvar a chamada."], type: "error" });
        })
        .finally(() => {
          this.savingTagline = false;
        });
    },
    generate() {
      if (this.generating) return;
      const msg = this.imageUrl
        ? "Isso vai gerar uma nova arte com IA e substituir a capa atual. Continuar?"
        : "Gerar a arte da capa com IA a partir do município da conta?";
      if (!confirm(msg)) return;
      this.generating = true;
      PmauService.GenerateCover(this.contaId)
        .then(r => {
          this.imageUrl = r.data.image_url ? r.data.image_url + "?t=" + Date.now() : null;
          this.generatedAt = r.data.generated_at || null;
          this.$notify({ data: ["Capa gerada com sucesso."], type: "success" });
        })
        .catch(e => {
          const detail =
            e && e.response && e.response.data && e.response.data.detail
              ? e.response.data.detail
              : "Erro ao gerar a capa.";
          this.$notify({ data: [detail], type: "error", duration: 8000 });
        })
        .finally(() => {
          this.generating = false;
        });
    },
    exportDocx() {
      if (this.exporting) return;
      this.exporting = true;
      PmauService.ExportCover(this.contaId)
        .then(res => {
          const url = window.URL.createObjectURL(new Blob([res.data]));
          const a = document.createElement("a");
          a.href = url;
          a.download = "Capa do PMAU.docx";
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(url);
        })
        .catch(() => {
          this.$notify({ data: ["Erro ao exportar a capa."], type: "error" });
        })
        .finally(() => {
          this.exporting = false;
        });
    },
    formatDate(iso) {
      const d = new Date(iso);
      return isNaN(d) ? iso : d.toLocaleString("pt-BR");
    }
  }
};
</script>

<style scoped>
.btn-word {
  background-color: #295294;
  border-color: #295294;
  color: #fff;
}
.btn-word:hover:not(:disabled) {
  background-color: #1f4074;
  border-color: #1f4074;
  color: #fff;
}
.btn-word:disabled {
  opacity: 0.65;
}
</style>
